import { LayerInfo } from "src/common/managers/Types";
import { useContext, useEffect, useRef, useState } from "react";
import { MapitLayerFactory } from "./MapitLayerFactory";
import { actionSetMapInteractionState, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { MitLayerHandle } from "src/managers/MapFacade";
import { featureCollection, point } from "@turf/helpers";
import { bearing, centroid, distance, midpoint, transformRotate } from "@turf/turf";
import { MapMouseEvent, Point, LngLat } from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { FeatureCollection, Position } from "geojson";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { createPortal } from "react-dom";
import { GlassButton } from "./GlassButtons";
import { GlassTextInputTransformer } from "./GlassInput";

const layerPrefixes = "ImageOverlayEditor"
export function ImageOverlayEditor(
   props: {
      layerInfo: LayerInfo,
      onChange: (newLayerInfo: LayerInfo) => void,
   }
) {
   let [opacity, setOpacity] = useState<number>(props.layerInfo.styling.opacity ?? 1);
   let localLayerHandler = useRef<MitLayerHandle | undefined>(undefined);
   let { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   let localJson = useRef<LayerInfo["geoJson"]>(props.layerInfo.geoJson);
   let lHoverState = useRef<string[]>([])
   let lAnchor = useRef<[number, number] | undefined>(undefined);
   let lDelta = useRef<LayerInfo["geoJson"] | null>(null);
   let svgRef = useRef<SVGSVGElement>(null);
   let activeMoveHandler = useRef<((ev: MapMouseEvent & object) => void) | null>(null);
   let activeEndHandler = useRef<((ev: MapMouseEvent | MouseEvent) => void) | null>(null);

   function matchBox(imgCoord: [number, number][], svg: SVGSVGElement) {
      svg.style.position = "absolute"
      svg.style.width = "100%"
      svg.style.height = "100%"

      svg.style.pointerEvents = "none";
      svg.innerHTML = "<path class='image-overlay-highlight' d='M " + imgCoord.map(c => {
         let xy = mapitState.map.getMapPop().project(c)
         return xy.x + " " + xy.y
      }).join(" L ") + " Z' />";
   }

   useEffect(() => {
      let { layerHandle } = MapitLayerFactory.createADataLayer(mapitState.map, props.layerInfo, mapitState.mapInfo, {});
      let basicMap = mapitState.map.getMapPop();
      localLayerHandler.current = layerHandle;
      if (localLayerHandler.current) {
         localLayerHandler.current.addTo(mapitState.map);
         basicMap.addSource("ImageOverlayEditor" + "Source", {
            type: "geojson",
            data: featureCollection(constructControlData(localJson.current) as any),
         })
      }
      constructControlLayers("ImageOverlayEditor" + "Source").forEach(layer => {
         basicMap.addLayer(layer);
         basicMap.on("mouseenter", layer.id, () => {
            basicMap.getCanvas().style.cursor = "crosshair";
            lHoverState.current.push("move");
         });
         basicMap.on("mouseleave", layer.id, () => {
            lHoverState.current.pop();
            if (lHoverState.current.length === 0) {
               basicMap.getCanvas().style.cursor = "";
            }
         });
      });
      const mouseDown = (e: MouseEvent & MapMouseEvent) => {
         let startPos = e.lngLat;
         if (e.originalEvent.buttons > 3) return; // ignore right click or middle click
         let isLeftButton = (e.originalEvent.buttons === 1)
         e.target.queryRenderedFeatures(e.point).forEach((feature: any) => {
            if (feature.layer.id.startsWith(layerPrefixes)) {
               if (e._defaultPrevented) return;
               console.log(feature.layer.id)
               e.preventDefault();
               let editType = !isLeftButton ? "scale" : "drag";
               let initial = localJson.current;
               let pivot = structuredClone(lAnchor.current) ?? centroid(initial).geometry.coordinates;
               if (editType === "drag") {
                  lAnchor.current = undefined;
               }

               let isDragging = true;
               const move = (ev: MapMouseEvent & object) => {
                  if (!isDragging) return;
                  let deltaTransform = transAnchorForm(editType, startPos, pivot, ev.lngLat, initial);
                  lDelta.current = deltaTransform;
                  if (lAnchor.current && editType === "drag") {
                     lAnchor.current = (transAnchorForm("drag", startPos, pivot, ev.lngLat, featureCollection([point(lAnchor.current)])) as any).features[0].geometry.coordinates as [number, number];
                     console.log("Anchor moved to", lAnchor.current);
                  }
                  if (svgRef.current) matchBox(polyToImgCoords(lDelta.current), svgRef.current);
                  (basicMap.getSource(localLayerHandler.current?.getSourceId() || "") as maplibregl.ImageSource)?.setCoordinates(polyToImgCoords(deltaTransform) as any);
                  (basicMap.getSource("ImageOverlayEditor" + "Source") as maplibregl.GeoJSONSource)?.setData(featureCollection(constructControlData(deltaTransform, lAnchor.current) as any));
               }

               const endDrag = (ev: MapMouseEvent | MouseEvent) => {
                  if (!isDragging) return;
                  isDragging = false;

                  // Remove event listeners
                  basicMap.off("mousemove", move);
                  window.removeEventListener("mouseup", endDrag as EventListener);

                  // Clear refs
                  activeMoveHandler.current = null;
                  activeEndHandler.current = null;

                  // Finalize the drag operation
                  let finalLngLat: LngLat;
                  if (ev instanceof MouseEvent) {
                     // Mouse released outside map - use last known position
                     const rect = basicMap.getCanvas().getBoundingClientRect();
                     const point = new Point(ev.clientX - rect.left, ev.clientY - rect.top);
                     finalLngLat = basicMap.unproject(point);
                  } else {
                     finalLngLat = ev.lngLat;
                  }

                  let deltaTransform = transAnchorForm(editType, startPos, pivot, finalLngLat, initial);
                  lDelta.current = deltaTransform;
                  if (editType === "drag") {
                     lAnchor.current = [finalLngLat.lng, finalLngLat.lat];
                     (basicMap.getSource("ImageOverlayEditor" + "Source") as maplibregl.GeoJSONSource)?.setData(featureCollection(constructControlData(deltaTransform, lAnchor.current) as any));
                  }
                  localJson.current = deltaTransform;
                  localLayerHandler.current?.updateSource(mapitState.map, { coordinates: polyToImgCoords(deltaTransform) as any });
               }

               // Store handlers in refs for cleanup
               activeMoveHandler.current = move;
               activeEndHandler.current = endDrag;

               // Add both map-level and window-level mouseup listeners
               basicMap.on("mousemove", move);
               basicMap.once("mouseup", endDrag);
               window.addEventListener("mouseup", endDrag as EventListener, { once: true });
            };
         });
      }


      basicMap.on("mousedown", mouseDown);
      return () => {
         // Clean up any active drag operations
         if (activeMoveHandler.current) {
            basicMap.off("mousemove", activeMoveHandler.current);
            activeMoveHandler.current = null;
         }
         if (activeEndHandler.current) {
            window.removeEventListener("mouseup", activeEndHandler.current as EventListener);
            activeEndHandler.current = null;
         }

         constructControlLayers("ImageOverlayEditor" + "Source").forEach(layer => {
            if (basicMap.getLayer(layer.id)) basicMap.removeLayer(layer.id);
         });
         basicMap.off("mousedown", mouseDown);
         basicMap.getCanvas().style.cursor = "";
         basicMap.removeSource("ImageOverlayEditor" + "Source");
         if (localLayerHandler.current) {
            if (props.layerInfo.layerId == -1) {
               localLayerHandler.current.clearLayers(mapitState.map);
            }
            localLayerHandler.current = undefined;
         }
      };
   }, [])

   useEffect(() => {
      mapitDispatch(actionSetMapInteractionState(MapInteractionState.Override));
      return () => {
         mapitDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {

      let map = mapitState.map.getMapPop();
      const movMap = () => {
         if (svgRef.current) {
            matchBox(polyToImgCoords(lDelta.current || localJson.current), svgRef.current);
         }
      }
      map.on("move", movMap);
      return () => {
         map.off("move", movMap);
      };
   })


   return (<><div>
      <div className="image-overlay-preview" style={{
         padding: "12px"
      }}>
         <img src={props.layerInfo.geoJson?.features[0].properties._imgUrl as string} style={{ width: "100%" }} />
      </div>
      <GlassTextInputTransformer label={"Gennemsigtighed"} >
         <input type="range" min={0} max={1} step={0.01}
            value={opacity}
            onChange={(e) => {
               setOpacity(parseFloat(e.currentTarget.value));
               localLayerHandler.current?.getLayerIds().forEach(layerId => {
                  mapitState.map.getMapPop().setPaintProperty(layerId, "raster-opacity", parseFloat(e.currentTarget.value));
               });
            }} />
      </GlassTextInputTransformer>
      <GlassButton onClick={() => {
         props.onChange({
            ...props.layerInfo,
            styling: {
               ...props.layerInfo.styling,
               opacity: opacity,
            },
            geoJson: localJson.current,
         });
      }}>Gem ændringer</GlassButton>
   </div>
      {createPortal(
         <>
            <svg style={{ pointerEvents: "none" }} ref={svgRef} />
         </>, document.getElementById("map") || document.body
      )}
   </>
   )
}

function transAnchorForm(
   editType: string,
   startPos: maplibregl.LngLat,
   pivot: Position,
   currentPos: maplibregl.LngLat,
   initial: LayerInfo["geoJson"],
): FeatureCollection {
   let result = structuredClone(initial);
   let distStart = distance([startPos.lng, startPos.lat], [pivot[0], pivot[1]]);
   let distCurrent = distance([currentPos.lng, currentPos.lat], [pivot[0], pivot[1]]);
   let scale = distCurrent / distStart;
   let transLateX = (startPos.lng - currentPos.lng);
   let transLateY = (startPos.lat - currentPos.lat);
   if (editType === "drag") {
      result = GenerateGeomUtils.geoJsonCoordsTransformer<number>(initial, (coord) => {
         return [coord[0] - transLateX, coord[1] - transLateY];
      });
   }
   if (editType.includes("scale")) {
      let angleStart = bearing([pivot[0], pivot[1]], [startPos.lng, startPos.lat]);
      let angleCurrent = bearing([pivot[0], pivot[1]], [currentPos.lng, currentPos.lat]);
      let angleDiff = angleCurrent - angleStart;
      result = transformRotate(initial, angleDiff, { pivot: pivot });
      result = GenerateGeomUtils.geoJsonCoordsTransformer<number>(result, (coord) => {
         return [
            pivot[0] + (coord[0] - pivot[0]) * scale,
            pivot[1] + (coord[1] - pivot[1]) * scale,
         ]
      });
   }
   if (result.features[0].geometry.coordinates?.length !== 1) {
      return result;
   }
   if (result.features[0].geometry.coordinates[0].flat?.().filter((a) => (a !== Infinity)).length !== 10) {
      return initial;
   }
   return result;
}
function polyToImgCoords(featureCollection: any): [number, number][] {
   return featureCollection.features[0].geometry.coordinates[0].slice(0, 4) as [number, number][]
}

function constructControlData(geoJson: LayerInfo["geoJson"], anchor?: [number, number]): GeoJSON.Feature[] {
   return [
      ...geoJson.features.map((a, idx) => ({ ...a, id: idx, properties: { editType: "drag" } })),
      ...geoJson.features[0].geometry.coordinates[0].slice(0, 4).map((coord, idx) => ({
         type: "Feature",
         id: 100 + idx,
         geometry: { type: "Point", coordinates: coord },
         properties: { editType: "scale", cornerIndex: idx }
      })),
      ...geoJson.features[0].geometry.coordinates[0].slice(0, 4).map((coord, idx, arr) => ({
         type: "Feature",
         id: 200 + idx,
         geometry: { type: "Point", coordinates: midpoint(coord, arr[(idx + 1) % arr.length])!.geometry.coordinates },
         properties: { editType: "rotate", edgeIndex: idx }
      })),
      ...(anchor ? [{
         type: "Feature",
         id: 300,
         geometry: { type: "Point", coordinates: anchor },
         properties: { editType: "anchor" }
      }] : [])
   ]
}


function constructControlLayers(sourceId: string): maplibregl.LayerSpecification[] {
   return [
      {
         id: layerPrefixes + "DragLayer",
         type: "fill",
         source: sourceId,
         filter: ["==", ["get", "editType"], "drag"],
         paint: {
            "fill-color": "#d6c251",
            "fill-opacity": 0.0,
            "fill-outline-color": "#d6c251",
         },
      },
      {
         id: layerPrefixes + "AnchorLayer",
         type: "symbol",
         source: sourceId,
         filter: ["==", ["get", "editType"], "anchor"],
         layout: {
            "icon-image": "mit-patt-char-📍",
            "icon-size": 1.8,
            "icon-anchor": "bottom",
            "icon-offset": [0, 9],
         }

      }
   ];
}
